import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * RenewalRunway — the account's clock, drawn instead of listed.
 *
 * The Renewals widget was six rows and the sentence "44 more". A list can only
 * ever show its first few, so the shape of the window — where the money
 * clusters, how much of it is competitor paper, what lands inside the next
 * quarter — was invisible no matter how tall you made the card.
 *
 * Same query, plotted: x is the expiry date, y is contract value on a log
 * scale (a set holding both $3K and $8.9M is unreadable linearly), colour says
 * ours vs theirs, and the first 90 days are shaded because that band is the
 * one with a deadline. Every mark is clickable — the endpoint already returns
 * `poNumbers` per contract.
 *
 * Presentational: data via props, no fetching, colours from CSS variables.
 */
import { useMemo } from 'react';
import { ChartDrillList } from './ChartDrillList.js';
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ReferenceArea, ResponsiveContainer, Cell, } from 'recharts';
const defaultFormatValue = (v) => {
    if (v >= 1000000000)
        return `$${(v / 1000000000).toFixed(1)}B`;
    if (v >= 1000000)
        return `$${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000)
        return `$${(v / 1000).toFixed(0)}K`;
    return `$${Math.round(v)}`;
};
const DAY = 86400000;
/** Log scales cannot plot zero, and a $0 contract is a real row in this data. */
const FLOOR = 1;
function RunwayTooltip({ active, payload, formatValue, }) {
    if (!active || !payload || payload.length === 0)
        return null;
    const d = payload[0].payload;
    const when = d.endDate
        ? new Date(d.endDate).toLocaleDateString(undefined, {
            month: 'short', day: 'numeric', year: 'numeric',
        })
        : null;
    return (_jsxs("div", { className: "cht cht--compact", children: [_jsxs("div", { className: "cht__row-inline", children: [_jsx("strong", { className: "cht__strong", children: d.vendor }), d.ours && (_jsx("span", { className: "cht__ours", children: "ours" }))] }), d.product && (_jsx("div", { className: "cht__sub", children: d.product })), _jsx("div", { className: "cht__figure", children: d.contractValueFormatted || formatValue(d.v) }), _jsxs("div", { className: "cht__sub", children: [when, d.daysUntil != null && ` · ${d.daysUntil}d`] }), d.poNumbers && d.poNumbers.length > 0 && (_jsxs("div", { className: "cht__hint", children: ["Click for PO ", d.poNumbers[0], d.poNumbers.length > 1 ? ` (+${d.poNumbers.length - 1})` : ''] }))] }));
}
export const RenewalRunway = ({ renewals, height = 260, fill = false, months = 24, onPick, formatValue = defaultFormatValue, }) => {
    const { data, domain, urgentEnd, ticks } = useMemo(() => {
        const now = Date.now();
        const end = new Date();
        end.setMonth(end.getMonth() + months);
        const points = (renewals || [])
            .filter((r) => r.endDate)
            .map((r) => ({
            ...r,
            t: new Date(r.endDate).getTime(),
            v: Math.max(Number(r.contractValue) || 0, FLOOR),
            urgent: (r.daysUntil ?? Infinity) <= 90,
        }))
            .filter((d) => Number.isFinite(d.t));
        // Explicit quarter-start ticks. Left to choose its own, recharts picked
        // timestamps close enough together that the first four all formatted to
        // the same month label — four identical ticks reading "Sep 26".
        const marks = [];
        const cursor = new Date();
        cursor.setDate(1);
        cursor.setHours(0, 0, 0, 0);
        cursor.setMonth(cursor.getMonth() - (cursor.getMonth() % 3) + 3);
        while (cursor.getTime() <= end.getTime()) {
            marks.push(cursor.getTime());
            cursor.setMonth(cursor.getMonth() + 3);
        }
        return {
            data: points,
            domain: [now, end.getTime()],
            urgentEnd: now + 90 * DAY,
            ticks: marks,
        };
    }, [renewals, months]);
    if (data.length === 0) {
        return (_jsx("div", { className: `chs__empty${fill ? ' chs__empty--fill' : ''}`, style: fill ? undefined : { '--h': `${height}px` }, children: "Nothing expires in this window." }));
    }
    const tickDate = (t) => new Date(t).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
    return (_jsxs(_Fragment, { children: [_jsx(ResponsiveContainer, { width: "100%", height: fill ? '100%' : height, className: fill ? 'ts-chart-fill' : undefined, children: _jsxs(ScatterChart, { margin: { top: 12, right: 12, left: 4, bottom: 4 }, children: [_jsx(CartesianGrid, { strokeDasharray: "3 3", stroke: "var(--color-border, var(--border, #e5e7eb))" }), _jsx(ReferenceArea, { x1: domain[0], x2: Math.min(urgentEnd, domain[1]), fill: "var(--color-danger, #dc2626)", fillOpacity: 0.07, ifOverflow: "hidden" }), _jsx(XAxis, { type: "number", dataKey: "t", domain: domain, scale: "time", ticks: ticks, tickFormatter: tickDate, axisLine: false, tickLine: false, tick: { fill: 'var(--color-text-muted, var(--text-muted, #9ca3af))', fontSize: 11 }, minTickGap: 24 }), _jsx(YAxis, { type: "number", dataKey: "v", scale: "log", domain: ['auto', 'auto'], tickFormatter: formatValue, axisLine: false, tickLine: false, tick: { fill: 'var(--color-text-muted, var(--text-muted, #9ca3af))', fontSize: 11 }, width: 54 }), _jsx(ZAxis, { type: "number", dataKey: "lineCount", range: [60, 320] }), _jsx(Tooltip, { content: _jsx(RunwayTooltip, { formatValue: formatValue }), cursor: { strokeDasharray: '3 3' } }), _jsx(Scatter, { data: data, isAnimationActive: false, onClick: onPick ? (entry) => {
                                const d = entry?.payload;
                                if (d)
                                    onPick(d);
                            } : undefined, cursor: onPick ? 'pointer' : undefined, children: data.map((d, i) => (_jsx(Cell, { 
                                // Ours is the accent; competitor paper inside 90 days is the
                                // thing to act on, so it carries the danger colour and
                                // everything else stays neutral rather than shouting.
                                fill: d.ours
                                    ? 'var(--accent, #3b82f6)'
                                    : d.urgent
                                        ? 'var(--color-danger, #dc2626)'
                                        : 'var(--color-text-muted, var(--text-muted, #9ca3af))', fillOpacity: d.ours || d.urgent ? 0.85 : 0.5, stroke: d.ours ? 'var(--accent, #3b82f6)' : 'none' }, i))) })] }) }), _jsx(ChartDrillList, { label: "Renewal runway \u2014 open the contract behind each mark", drills: onPick ? renewals.map((r, i) => ({
                    key: `${r.vendor}-${r.endDate || i}`,
                    label: `${r.vendor}${r.product ? `, ${r.product}` : ''}${r.endDate ? `, lapses ${r.endDate}` : ''}${r.contractValueFormatted ? `, ${r.contractValueFormatted}` : ''} — open the contract`,
                    onSelect: () => onPick(r),
                })) : [] })] }));
};
export default RenewalRunway;
